import { useEffect, useRef, useState } from 'react';
import { Dialog } from './dialog';
import { Button } from 'primereact/button';
import { LearnerCertificateDto } from '../../general/dtos';
import { useApi } from '../../providers/api-provider';
import './certificate-dialog.css';
import { CertificatePrintRequestDialog } from './certificate-print-request-dialog';
import Config from '../../general/config';

export function CertificateDialog({ certificate, onClose }: { certificate: LearnerCertificateDto; onClose: () => void }) {
    const api = useApi();
    const url = api!.getCertificatePublicUrl(certificate.key);
    const imageUrl = api!.getCertificateImageUrl(certificate.key);
    const [loading, setLoading] = useState(true);
    const [imageError, setImageError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [copyStatus, setCopyStatus] = useState('');
    const [copyFailed, setCopyFailed] = useState(false);
    const [copying, setCopying] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState('');
    const [orderingPrint, setOrderingPrint] = useState(false);
    const downloadController = useRef<AbortController | null>(null);
    const downloadUrls = useRef(new Map<string, ReturnType<typeof setTimeout>>());

    useEffect(() => {
        const urls = downloadUrls.current;
        return () => {
            downloadController.current?.abort();
            urls.forEach((timer, objectUrl) => { clearTimeout(timer); URL.revokeObjectURL(objectUrl); });
            urls.clear();
        };
    }, []);

    const download = async () => {
        if (!api || downloadController.current) return;
        const controller = new AbortController();
        downloadController.current = controller;
        setDownloading(true); setDownloadError('');
        try {
            const blob = await api.downloadCertificate(certificate.key, controller.signal);
            if (controller.signal.aborted) return;
            const objectUrl = URL.createObjectURL(blob);
            downloadUrls.current.set(objectUrl, setTimeout(() => {
                URL.revokeObjectURL(objectUrl); downloadUrls.current.delete(objectUrl);
            }, 1000));
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = certificate.download_name ?? `Triad-Certificate-${certificate.key}.png`;
            document.body.appendChild(link);
            try { link.click(); } finally { link.remove(); }
        } catch {
            if (!controller.signal.aborted) setDownloadError('Could not download the certificate. Please try again.');
        } finally {
            downloadController.current = null;
            if (!controller.signal.aborted) setDownloading(false);
        }
    };

    const copyLink = async () => {
        setCopying(true);
        try {
            await navigator.clipboard.writeText(url);
            setCopyStatus('Public link copied.');
            setCopyFailed(false);
        } catch {
            setCopyStatus('Could not copy automatically. Select and copy the link below.');
            setCopyFailed(true);
        } finally {
            setCopying(false);
        }
    };

    return <><Dialog visible modal header={`Certificate — ${certificate.course_name}`} onHide={onClose}
        draggable={false} resizable={false} className="certificate-dialog"
        style={{ width: 'calc(100vw - 32px)', maxWidth: 'calc(100vw - 32px)', height: 'calc(100vh - 32px)', maxHeight: 'calc(100vh - 32px)' }}
        footer={<div className="flex justify-content-end flex-wrap gap-2">
            <Button label="Download" icon="pi pi-download" onClick={() => void download()} loading={downloading} />
            <Button label="Copy public link" icon="pi pi-copy" onClick={() => void copyLink()} loading={copying} />
            {Config.allowOrderCertificates && <Button label="Order printed copy" icon="pi pi-print" onClick={() => setOrderingPrint(true)} />}
            <Button label="Close" icon="pi pi-times" outlined onClick={onClose} />
        </div>}>
        {loading && <p role="status">Generating your certificate…</p>}
        {imageError ? <div role="alert">Could not display the certificate. Please try again.
            <Button label="Retry image" text onClick={() => { setImageError(false); setLoading(true); setAttempt(value => value + 1); }} />
        </div> : <div className="certificate-image-area">
            <img key={attempt} src={imageUrl} alt={`Certificate of completion for ${certificate.course_name}`}
                style={{ visibility: loading ? 'hidden' : 'visible' }} onLoad={() => setLoading(false)}
                onError={() => { setLoading(false); setImageError(true); }} />
        </div>}
        {copyStatus && <p role={copyFailed ? 'alert' : 'status'}>{copyStatus}</p>}
        {downloadError && <p role="alert">{downloadError}</p>}
        {copyFailed && <input aria-label="Public certificate link" value={url} readOnly onFocus={event => event.target.select()} />}
    </Dialog>
        {Config.allowOrderCertificates && orderingPrint && <CertificatePrintRequestDialog certificate={certificate} onClose={() => setOrderingPrint(false)} />}
    </>;
}
